import { useEffect, useRef, useState } from "react";
import COUNTRIES, { PRIORITY, flagSrc } from "./countries";
import { addOutsidePress, removeOutsidePress } from "./outsidePress";

// Flag image (flagcdn.com PNG with a 2x source for retina).
function Flag({ iso, className = "" }) {
  const code = (iso || "").toLowerCase();
  return (
    <img
      src={flagSrc(code, 40)}
      srcSet={`${flagSrc(code, 80)} 2x`}
      width={20}
      height={15}
      alt=""
      loading="lazy"
      className={`inline-block h-[15px] w-5 shrink-0 rounded-[2px] object-cover ring-1 ring-black/5 ${className}`}
    />
  );
}

function CountryRow({ c, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-gray-50 ${active ? "bg-gray-50" : ""}`}
    >
      <Flag iso={c.c} />
      <span className="flex-1 truncate text-gray-800">{c.n}</span>
      <span className="text-gray-400">+{c.d}</span>
    </button>
  );
}

// Split an existing value ("+91 98765..." or bare digits) into a country + number.
// `fallbackIso` seeds the country when the value is empty (community default).
function parsePhone(value, fallbackIso = "US") {
  const v = String(value || "").trim();
  if (!v) return { iso: fallbackIso, number: "" };
  if (v.startsWith("+")) {
    const digits = v.slice(1).replace(/\D/g, "");
    let best = null;
    for (const c of COUNTRIES) {
      if (!digits.startsWith(c.d)) continue;
      const better =
        !best ||
        c.d.length > best.d.length ||
        (c.d.length === best.d.length && PRIORITY.includes(c.c) && !PRIORITY.includes(best.c));
      if (better) best = c;
    }
    if (best) return { iso: best.c, number: digits.slice(best.d.length) };
    return { iso: "US", number: digits };
  }
  return { iso: "US", number: v.replace(/\D/g, "") };
}

/**
 * Phone field with a country dial-code selector (flag + code) and a searchable
 * dropdown. Emits the combined value ("+<dial> <digits>") to `onChange`.
 * The dropdown is rendered as a fixed overlay anchored to the field, so it never
 * expands a scroll container or gets clipped by it.
 */
export default function PhoneField({ value, onChange, disabled, primary = "#0274F7", label = "Phone", optional = true, defaultCountry = "US" }) {
  const initial = parsePhone(value, defaultCountry);
  const [iso, setIso] = useState(initial.iso);
  const [number, setNumber] = useState(initial.number);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [coords, setCoords] = useState(null); // { left, top, width } for the overlay
  const boxRef = useRef(null);
  const popRef = useRef(null);
  const searchRef = useRef(null);

  const country = COUNTRIES.find((c) => c.c === iso) || COUNTRIES.find((c) => c.c === "US");

  // Anchor the fixed overlay to the field, and keep it aligned on scroll/resize.
  useEffect(() => {
    if (!open) return;
    const place = () => {
      const r = boxRef.current?.getBoundingClientRect();
      if (r) setCoords({ left: r.left, top: r.bottom + 6, width: r.width });
    };
    place();
    const t = setTimeout(() => searchRef.current?.focus(), 0);
    const onDoc = (e) => {
      if (boxRef.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true); // capture: catches a modal's own scroll
    addOutsidePress(onDoc);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      removeOutsidePress(onDoc);
    };
  }, [open]);

  const emit = (dial, num) => {
    const digits = String(num || "").replace(/\D/g, "");
    onChange(digits ? `+${dial} ${digits}` : "");
  };
  const pickCountry = (c) => {
    setIso(c.c);
    setOpen(false);
    setQuery("");
    emit(c.d, number);
  };
  const onNumberChange = (v) => {
    const digits = v.replace(/\D/g, "");
    setNumber(digits);
    emit(country.d, digits);
  };

  // Country search. The digits clause is gated on there actually BEING digits:
  // `"91".includes("")` is true, so an all-letters query like "india" used to
  // match every country through that branch and the list came back unfiltered —
  // searching by name looked broken while searching by code worked. Dial codes
  // match by prefix (typing "91" means India, not "every code containing a 9 or
  // a 1"), and a 2-letter query also matches the ISO code so "IN" / "AE" work.
  const q = query.trim().toLowerCase();
  const digits = q.replace(/\D/g, "");
  const match = (c) => {
    if (!q) return true;
    if (c.n.toLowerCase().includes(q)) return true;
    if (q.length === 2 && c.c.toLowerCase() === q) return true;
    return !!digits && c.d.startsWith(digits);
  };
  const priorityList = PRIORITY.map((code) => COUNTRIES.find((c) => c.c === code)).filter(Boolean).filter(match);
  const restList = COUNTRIES.filter((c) => !PRIORITY.includes(c.c)).filter(match);
  const active = focused || open;

  return (
    <label className="block">
      <span className="text-[13px] font-medium text-gray-600 mb-1.5 flex items-center gap-1">
        {label}
        {optional
          ? <span className="text-gray-400 font-normal">(optional)</span>
          : <span className="text-red-500">*</span>}
      </span>
      <div
        ref={boxRef}
        className="flex items-stretch rounded-xl border transition-colors"
        style={{
          borderColor: active ? primary : "rgb(var(--c-gray-200))",
          boxShadow: active ? `0 0 0 3px ${primary}22` : "none",
          backgroundColor: active ? "#fff" : "rgb(var(--c-surface-sunken))",
        }}
      >
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-1.5 rounded-l-xl pl-3.5 pr-2 text-sm text-gray-700 hover:bg-black/[0.03] disabled:opacity-60"
        >
          <Flag iso={country.c} />
          <span className="font-medium">+{country.d}</span>
          <svg
            className={`w-3.5 h-3.5 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        <span className="my-2 w-px shrink-0 bg-gray-200" />
        <input
          type="tel"
          inputMode="tel"
          value={number}
          disabled={disabled}
          placeholder="Phone number"
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onChange={(e) => onNumberChange(e.target.value)}
          className="w-full bg-transparent border-0 appearance-none py-2.5 px-3 text-sm text-gray-800 outline-none focus:outline-none focus:ring-0 shadow-none placeholder-gray-400"
        />
      </div>

      {/* z-index has to clear Leaflet, not just the page: the location preview
          map sits in the same stacking context (inside the modal) and its panes
          are z-index 400-700, so at z-[60] the tiles and the marker painted
          straight over this list. */}
      {open && coords && (
        <div
          ref={popRef}
          style={{ position: "fixed", left: coords.left, top: coords.top, width: coords.width }}
          className="z-[10001] overflow-hidden rounded-xl border border-gray-200 bg-surface shadow-2xl"
        >
          {/* The search box's focus ring is the community's theme colour, like
              every other field here. Without the explicit inline style + the
              ring/outline resets, @tailwindcss/forms' base styles win and paint
              the stock blue-600 ring, which is what showed through before. */}
          <div className="border-b border-gray-100 p-2">
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              placeholder="Search country or code"
              className="w-full rounded-lg border px-3 py-1.5 text-sm text-gray-800 outline-none focus:outline-none focus:ring-0 shadow-none"
              style={{
                borderColor: searchFocused ? primary : "rgb(var(--c-gray-200))",
                boxShadow: searchFocused ? `0 0 0 3px ${primary}22` : "none",
              }}
            />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {priorityList.map((c) => (
              <CountryRow key={c.c} c={c} active={c.c === iso} onClick={() => pickCountry(c)} />
            ))}
            {priorityList.length > 0 && restList.length > 0 && <div className="my-1 border-t border-gray-100" />}
            {restList.map((c) => (
              <CountryRow key={c.c} c={c} active={c.c === iso} onClick={() => pickCountry(c)} />
            ))}
            {priorityList.length === 0 && restList.length === 0 && (
              <p className="px-3 py-4 text-center text-sm text-gray-400">No matching countries</p>
            )}
          </div>
        </div>
      )}
    </label>
  );
}
